import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/useAuth';
import { clearFitLiveIntent, markFitLiveIntent } from '../utils/fitLiveModule';
import { modulePublicLoginUrl } from '../utils/entryHost';

/**
 * Modal claro Fit Live: login/registro in-place (sin ir al login oscuro legacy).
 */
export default function FitLiveGateModal({
  open,
  reason = 'class',
  trainerLabel = '',
  onClose,
}) {
  const { login, register } = useAuth();
  const [tab, setTab] = useState(reason === 'register' ? 'register' : 'login');
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTab(reason === 'register' ? 'register' : 'login');
    setError('');
  }, [open, reason]);

  if (!open || typeof document === 'undefined') return null;

  const reasonCopy = {
    class: 'Para reservar esta clase',
    replay: 'Para ver este replay',
    reserve: 'Para reservar',
    login: 'Bienvenido a Fit Live',
    register: 'Crea tu cuenta Fit Live',
    open: 'Accede a Fit Live',
  }[reason] || 'Accede a Fit Live';

  const finishOk = async () => {
    onClose?.({ ok: true });
  };

  const isDuplicateEmail = (err) => {
    const msg = String(err?.response?.data?.error || err?.message || '');
    const status = err?.response?.status;
    return status === 409 || /ya existe una cuenta/i.test(msg);
  };

  const loginWithForm = async () => {
    const mail = String(email).trim().toLowerCase();
    if (!mail || !password) {
      throw new Error('Escribe el correo y contraseña de tu cuenta del ecosistema.');
    }
    if (String(password).length < 8) {
      throw new Error('La contraseña debe tener al menos 8 caracteres');
    }
    if (tab === 'register') {
      const name = String(nombre).trim();
      if (!name) throw new Error('Escribe tu nombre');
      try {
        await register({
          nombre: name,
          email: mail,
          password,
          module_access: { fit_live: true, live_classes: true },
        });
        return login(mail, password);
      } catch (regErr) {
        if (!isDuplicateEmail(regErr)) throw regErr;
        try {
          return await login(mail, password);
        } catch {
          setTab('login');
          throw new Error('Este correo ya tiene cuenta. Inicia sesión con tu contraseña.');
        }
      }
    }
    return login(mail, password);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      markFitLiveIntent('open');
      await loginWithForm();
      await finishOk();
    } catch (err) {
      clearFitLiveIntent();
      const msg = err?.response?.data?.error || err?.message || 'No se pudo completar';
      setError(String(msg));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fl-gate-overlay" role="presentation" onClick={() => !busy && onClose?.()}>
      <div
        className="fl-gate-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fl-gate-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        <button type="button" className="fl-gate-close" onClick={() => !busy && onClose?.()} aria-label="Cerrar">
          ×
        </button>

        <p className="fl-gate-kicker">ECOSISTEMA FIT · FIT LIVE</p>
        <h2 id="fl-gate-title">Entrena en vivo con Fit Live</h2>
        <p className="fl-gate-lead">
          {reasonCopy}
          {trainerLabel ? ` con ${trainerLabel}` : ''}
          , regístrate o inicia sesión.
        </p>

        <div className="fl-gate-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'register'}
            className={tab === 'register' ? 'is-active' : ''}
            onClick={() => { setTab('register'); setError(''); }}
          >
            Registrarme
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'login'}
            className={tab === 'login' ? 'is-active' : ''}
            onClick={() => { setTab('login'); setError(''); }}
          >
            Ya tengo cuenta
          </button>
        </div>

        <form className="fl-gate-form" onSubmit={onSubmit}>
          {tab === 'register' ? (
            <label>
              Nombre
              <input
                required
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Tu nombre"
                autoComplete="name"
              />
            </label>
          ) : null}
          <label>
            Correo
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              autoComplete="username"
            />
          </label>
          <label>
            Contraseña
            <input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              autoComplete={tab === 'register' ? 'new-password' : 'current-password'}
            />
          </label>

          {error ? <p className="fl-gate-error">{error}</p> : null}

          <button type="submit" className="fl-gate-submit" disabled={busy}>
            {busy
              ? 'Un momento…'
              : (tab === 'register' ? 'Crear mi cuenta' : 'Entrar a Fit Live')}
          </button>
        </form>

        <div className="fl-gate-doors">
          <p className="fl-gate-doors-label">¿Tienes Food Plan, un entrenador o academia?</p>
          <a className="fl-gate-door" href={modulePublicLoginUrl('food')}>
            Ingresar a Food Plan
          </a>
          <a className="fl-gate-door fl-gate-door--alt" href={modulePublicLoginUrl('training')}>
            Entrenadores
          </a>
          <a className="fl-gate-door fl-gate-door--academia" href={modulePublicLoginUrl('futbol')}>
            Academia
          </a>
          <p className="fl-gate-doors-hint">
            Academia abre el login de escuelas (mismo correo y contraseña del ecosistema).
          </p>
        </div>

        <p className="fl-gate-note">
          Al continuar aceptas los términos Fit Live. Las clases y replays se pagan con FitTokens.
        </p>
      </div>
    </div>,
    document.body,
  );
}
